import ResizeSensor from "css-element-queries/src/ResizeSensor";
import * as _ from "lodash";
import { inject, observer, Provider } from "mobx-react";
import { action, reaction } from "mobx";
import React from "react";
import { v4 } from "uuid";

import config from "../../config";
import Loader from "../../shared/Loader";
import AbstractPatients from "../Patients/AbstractPatients";
import MeasurementsWorker from "./SurveilPatient/measurements.worker.js";
import RenderWorker from "./SurveilPatient/render.worker.js";
import SurveilStore from "./SurveilStore";
import SurveilPatient from "./SurveilPatient/SurveilPatient";
import PatientList from "./PatientList";
import "./Surveil.scss";
import FullMonitor from "./FullMonitor";
import {
  hasOffscreenCanvas,
  getUserCores,
} from "../../shared/performanceUtils";
import SideNavLinks from "./SideNavLinks";
import CanvasWorker from "../Monitor/canvas.worker";
import canvasWorkerUtils from "../Monitor/canvasWorkerFactory";
import { MODULE_ID } from "../../shared/moduleIds";
import InfoDialog from "../AHI/InfoDialog";
import { AHI_REFRESH_DURATION } from "../AHI/constants";
import resizeObserver from "../../shared/resizeObserver";

const fontSizeMin = 6;
const fontSizeMax = 28;

class Surveil extends AbstractPatients {
  subRenderCue = [];
  measurementsTargets = {};
  disposers = [];
  eventTarget = new EventTarget();
  surveilPatientRefs = {};
  visible = true;
  constructor(props) {
    super(props);
    const appStore = this.props.store;
    this.store = new SurveilStore({
      listIndex: _.get(props.match, "params.listIndex"),
      appStore,
    });

    if (this.searchParams?.view) {
      this.selectLayout(null, Number(this.searchParams.view));
    }
    setImmediate(() => {
      if (this.searchParams?.zoom) {
        this.store.fontSize =
          (Number(this.searchParams.zoom) / 10) * (fontSizeMax - fontSizeMin) +
          fontSizeMin;
      } else {
        this.props.store.prefsPromise.then((prefs) => {
          this.store.fontSize = +_.get(
            prefs,
            "userPreferences.client.pm.multiPatientFontSize",
            18,
          );
        });
      }
      if (this.searchParams?.monitorZoom) {
        this.store.monitorZoom = Number(this.searchParams.monitorZoom);
      }
    });

    this.createRenderWorker();
    this.spawnCanvasWorkerPool();

    this.measurementsWorker = new MeasurementsWorker();
    this.measurementsWorker.addEventListener("error", function (event) {
      console.error("Measurements Worker Error => ", event);
    });

    this.surveil = true;
    this.selectPatient = this.selectPatient.bind(this);
    if (typeof this.searchParams?.phi !== "undefined") {
      try {
        this.store.showPHI = Boolean(JSON.parse(this.searchParams.phi));
      } catch (e) {
        console.error("invalid phi route value");
      }
    } else {
      (async () => {
        this.store.showPHI = await props.store.getPref("pm.showPHI");
      })();
    }
  }

  componentDidMount() {
    const canvas = this.canvas;
    (async (canvas) => {
      await this.store.renderWorkerReady;
      const offscreenCanvas = canvas.transferControlToOffscreen();
      this.renderWorker?.postMessage(
        {
          setupRenderer: {
            canvas: offscreenCanvas,
            surveilLeadBufferSeconds: config.pm.surveilLeadBufferSeconds,
            debug: config.debug,
            patientColors: this.store.colorMap.patientColors,
          },
        },
        [offscreenCanvas],
      );
      new ResizeSensor(this.patientsDiv, this.resizeRendererToDisplaySize);
    })(canvas);

    this.disposers.push(
      reaction(
        () => this.sweepMode,
        () => {
          this.store.resetMeasurements();
        },
      ),
    );
    this.resetOnWake();

    if (!this.store.allowSingle && !this.store.splitScreen) {
      this.store.splitScreen = 2;
    }

    this.store.postOpsLog({
      operationType: "multiPatientViewed",
      extraContext: {
        user: sessionStorage.username,
        timestamp: new Date().toISOString(),
      },
    });

    this.startPolling();
    this.disposers.push(() => this.stopPolling());

    if (config.pm.preventTimeout) {
      this.props.store.authStore.blockTimeout(this);
    }

    resizeObserver.observe(this.patientsDiv, (e) => {
      this.eventTarget.dispatchEvent(new CustomEvent("resize", e));
    });
  }
  componentDidUpdate() {
    if (!this.sweepMode && !this.store.splitScreen) {
      const asmpids = this.store.limitedPatients.map((p) => p.asmpid).join();
      if (asmpids && this.asmpids !== asmpids) {
        this.eventTarget.dispatchEvent(new CustomEvent("resize"));
      }
      this.asmpids = asmpids;
    } else {
      // this.asmpids is used to track the order of condensed monitors for the webgl renderer.
      this.asmpids = undefined;
    }
  }
  componentWillUnmount() {
    resizeObserver.unobserve(this.patientsDiv);
    this.renderCharts = _.noop;
    if (this.renderWorker) {
      this.renderWorker.addEventListener("message", (e) => {
        if (e.data.disposed) {
          this.renderWorker.terminate();
        }
      });
      this.renderWorker.postMessage({ dispose: true });
    }

    if (this.measurementsWorker) this.measurementsWorker.terminate();

    if (this.canvasWorkers) {
      // This has to be destructured because terminate modifies the canvasWorkers array, which causes forEach to exit early on Windows.
      [...this.canvasWorkers].forEach((worker) => worker.terminate());
    }
    clearInterval(this.refreshInterval);
    clearInterval(this.ahiRefreshInterval);
    this.props.store.authStore.unblockTimeout(this);
    this.disposers.forEach((dispose) => dispose());
  }

  render() {
    const patients = this.store.splitScreen
      ? this.store.splitScreenPatients
      : this.store.limitedPatients;
    return (
      <Provider surveilStore={this.store}>
        <div
          {...this.className({
            "surveil widget": 1,
            "patient-selected": this.store.patient && this.store.allowSingle,
            "split-screen": this.store.splitScreen,
            [`split-${this.store.splitScreen}`]: this.store.splitScreen,
            "suppress-single": !this.store.allowSingle,
            "sweep-mode": this.sweepMode,
            "ahi-enabled": config.ahi.enabled,
          })}
        >
          {this.SubNav({
            fontSize: true,
            splitScreen: true,
            allowSingle: this.store.allowSingle,
            ahiDescriptions: this.store.ahiDescriptions,
          })}

          {this.store.isTooManyBeds ? (
            <div className="warning">
              Too many beds have been selected for display. Only the first{" "}
              {this.store.maxPatients} may be viewed. Please adjust your filter
              settings.
            </div>
          ) : null}

          <div className="with-sidenav">
            <div className="side-nav">
              {
                <SideNavLinks
                  parent={this}
                  showPatientList={this.store.showPatientList}
                  splitScreen={this.store.splitScreen}
                  togglePatientList={this.togglePatientList}
                  selectLayout={this.selectLayout}
                />
              }
              <div className="patient-list-mask"></div>
              <PatientList
                show={this.store.showPatientList}
                surveilStore={this.store}
                module={MODULE_ID.PATIENT_MONITORING}
                onDragEnd={this.onDragEnd}
                onResort={this.onResort}
              />
              <div
                {...this.className({
                  "font-size": true,
                  disabled: !!this.store.splitScreen,
                })}
              >
                <React.Fragment>
                  <div className="large">A</div>
                  <input
                    className="rotated-range"
                    type="range"
                    value={this.store.fontSize}
                    orient="vertical"
                    onChange={this._changeFontSize}
                    disabled={!!this.store.splitScreen}
                    min={fontSizeMin}
                    max={fontSizeMax}
                    step="0.1"
                  />
                  <div className="small">A</div>
                </React.Fragment>
              </div>
              {this.store.ahiDescriptions?.length && (
                <div className="ahi-wrapper">
                  <InfoDialog
                    showText={true}
                    ahiDescriptions={this.store.ahiDescriptions.slice()}
                  />
                </div>
              )}
            </div>

            {this.store.splitScreen ? (
              <div className="split-screen-container">
                {patients.map((patient) => (
                  <FullMonitor
                    key={patient.asmpid}
                    parent={this}
                    patient={patient}
                    isEmbedded={true}
                    measurements={
                      this.store.measurements?.[`${patient.asmpid}`]
                    }
                    sweepModeEnabled={this.sweepMode}
                  />
                ))}
              </div>
            ) : null}

            <React.Fragment>
              <div
                className="surveil-inner"
                data-canvas-worker={this.store.canvasWorkerIds}
                ref={(ref) => (this.innerDivRef = ref)}
              >
                {this.store.focusMode &&
                patients.find((p) => p.asmpid === this.store?.patient?.asmpid)
                  ?.status ? (
                  <FullMonitor
                    parent={this}
                    patient={this.store?.limitedPatients.find(
                      (p) => p.asmpid === this.store?.patient?.asmpid,
                    )}
                    sweepModeEnabled={this.sweepMode}
                    key={this.store?.patient?.asmpid + "-focus"}
                  />
                ) : null}

                <div className="patients" ref={(el) => (this.patientsDiv = el)}>
                  {this.store.loadingConfiguration ? (
                    <Loader />
                  ) : patients && !this.store.splitScreen ? (
                    patients.map((patient) => {
                      return (
                        <SurveilPatient
                          onClick={() => this.selectPatient(patient)}
                          patient={patient}
                          key={patient.asmpid}
                          moduleSpecifics={this.store.moduleSpecifics(patient)}
                          renderer={this.renderer}
                          subRenderCue={this.subRenderCue}
                          plotMeasurementsCue={this.plotMeasurementsCue}
                          onRemove={this.onRemove(patient)}
                          fontSize={this.store.fontSize}
                          renderWorker={this.renderWorker}
                          measurementsWorker={this.measurementsWorker}
                          sweepMode={this.sweepMode}
                          canvasWorker={this.getCanvasWorkerInstance({
                            instanceId: `${patient.asmpid}-single-lead`,
                          })}
                          ahiData={this.store.ahiData[patient.asmpid]}
                          parent={this}
                          ref={this.setPatientRef}
                        />
                      );
                    })
                  ) : null}
                </div>
                <canvas
                  ref={(canvas) => (this.canvas = canvas)}
                  id="webgl-canvas"
                />
              </div>
            </React.Fragment>
          </div>
        </div>
      </Provider>
    );
  }

  setPatientRef = (ref) => {
    if (ref) this.surveilPatientRefs[ref.asmpid] = ref;
  };

  togglePHI = () => {
    this.store.showPHI = !this.store.showPHI;
    this.props.store.setPref("pm.showPHI", this.store.showPHI);
    this.store.postOpsLog({
      operationType: "togglePHI",
      extraContext: { showPHI: this.store.showPHI },
    });
  };

  get sweepMode() {
    return this.props.store.prefs?.userPreferences?.client?.pm?.sweep;
  }

  async startPolling() {
    this.stopPolling();
    while (this.store.loadingConfiguration) {
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
    this.refreshInterval = setInterval(() => {
      this.fetchMeasurements();
    }, 2000);
    if (config.ahi.enabled) {
      this.store.fetchAhiDescriptions();
      this.ahiRefreshInterval = setInterval(
        () => this.visible && this.store.fetchAhiData(true),
        AHI_REFRESH_DURATION,
      );
    }
    return this.fetchMeasurements();
  }
  stopPolling() {
    clearInterval(this.refreshInterval);
    clearInterval(this.ahiRefreshInterval);
  }
  fetchMeasurements = () => {
    if (!this.visible) return;
    let paused;
    if (!this.store.liveOnly) {
      paused = Object.entries(this.measurementsTargets)
        .filter(([, target]) => target.paused)
        .map(([asmpid]) => asmpid);
    }
    return this.store
      .fetchMeasurements({ paused })
      .then(async (measurements) => {
        if (!measurements) return;
        if (!this.visible) return;
        if (this.store.splitScreen) {
          let frameTime = performance.now();
          // TODO: use a Provider / Context instead of event targets.
          for (const asmpid in measurements) {
            if (!this.visible) return;
            const target = this.measurementsTargets[asmpid];
            if (target) {
              const event = new CustomEvent("measurements", {
                detail: measurements[asmpid],
              });
              target.dispatchEvent(event);
            }
            if (performance.now() - frameTime > 20)
              // todo: replace requestAnimationFrame with: requestIdleCallback(resolve));
              frameTime = await new Promise((resolve) =>
                requestAnimationFrame(resolve),
              );
          }
        } else {
          Object.entries(measurements).forEach(([asmpid, _measurements]) => {
            this.surveilPatientRefs[asmpid]?.plotMeasurements(_measurements);
          });
        }
      });
  };

  createRenderWorker() {
    const renderWorker = new RenderWorker();
    this.store.renderWorkerReady = new Promise((resolve) => {
      renderWorker.addEventListener(
        "message",
        (event) => {
          if (event.data?.status === "ready") {
            this.renderWorker = renderWorker;
            this.store.renderWorkerStatus = "ready";
            resolve(renderWorker);
          }
        },
        { once: true },
      );
    });
    renderWorker.addEventListener("error", function (event) {
      console.error("Render Worker Error => ", event);
    });
    renderWorker.addEventListener("messageerror", function (event) {
      console.error("Render Worker Error => ", event);
    });
    return this.store.renderWorkerReady;
  }
  spawnCanvasWorkerPool() {
    this.canvasWorkers = [];
    if (hasOffscreenCanvas) {
      const threadCount = Math.max(1, getUserCores() - 1);
      for (let i = 0; i < threadCount; i++) {
        this.startCanvasWorker(i);
      }
    } else {
      const canvasWorkerInstances = [];
      this.canvasWorkers.push({
        addEventListener: () => {},
        terminate: () => {},
        workerId: "slug",
        postMessage: (payload) => {
          let canvasWorkerInstance = canvasWorkerInstances.find(
            (instance) => instance.id === payload.instanceId,
          );
          if (payload.dropInstance && canvasWorkerInstance) {
            const index = canvasWorkerInstances.indexOf(canvasWorkerInstance);
            canvasWorkerInstances.splice(index, 1);
            return canvasWorkerInstance.destroy();
          }
          if (!canvasWorkerInstance) {
            canvasWorkerInstance = new canvasWorkerUtils({
              id: payload.instanceId,
            });
            canvasWorkerInstances.push(canvasWorkerInstance);
          }

          Object.entries(payload).forEach(([method, arg]) => {
            if (method === "instanceId") return;
            if (canvasWorkerInstance[method]) canvasWorkerInstance[method](arg);
            else console.error("Method not found", method, arg);
          });
        },
      });
    }
  }

  canvasWorkerIds = new Set();
  startCanvasWorker = (i) => {
    const timeoutMs = config.pm.canvasWorkerTimeout;
    const newCanvasWorker = new CanvasWorker();
    newCanvasWorker.addEventListener("error", (err) =>
      console.error("canvasWorker error", `thread ${i + 1}`, err),
    );
    newCanvasWorker.addEventListener("messageerror", (err) =>
      console.error("canvasWorker messageerror", `thread ${i + 1}`, err),
    );

    newCanvasWorker.addEventListener("message", (e) => {
      if (e.data === "pong") {
        clearTimeout(newCanvasWorker.timeout);
      }
      if (e.data.workerStarted) {
        newCanvasWorker.workerId = e.data.workerStarted.workerId;
        this.canvasWorkerIds.add(newCanvasWorker.workerId);
        this.store.canvasWorkerIds = Array.from(this.canvasWorkerIds).join();
        if (timeoutMs) {
          newCanvasWorker.pingInterval = setInterval(
            () => {
              if (!this.visible) return;
              newCanvasWorker.postMessage("ping");
              newCanvasWorker.timeout = setTimeout(() => {
                if (!this.visible) return;
                console.error("canvas worker timeout", `thread ${i + 1}`);
                newCanvasWorker.terminate();
                this.canvasWorkerIds.delete(newCanvasWorker.workerId);
                this.startCanvasWorker(i);
              }, timeoutMs);
            },
            _.random(timeoutMs * 4, timeoutMs * 5),
          );
        }
      }
    });

    this.canvasWorkers.push(newCanvasWorker);

    newCanvasWorker._terminate = newCanvasWorker.terminate;
    const surveil = this;
    newCanvasWorker.terminate = () => {
      clearInterval(newCanvasWorker.pingInterval);
      clearTimeout(newCanvasWorker.timeout);
      newCanvasWorker._terminate();

      Array.from(surveil.canvasWorkerInstanceMap.entries())
        .filter(([instance]) => {
          return instance.worker === newCanvasWorker;
        })
        .forEach(([instanceId]) => {
          surveil.canvasWorkerInstanceMap.delete(instanceId);
        });
      _.remove(surveil.canvasWorkers, newCanvasWorker);
    };
  };

  canvasWorkerIndex = 0;
  canvasWorkerInstanceMap = new Map();
  getCanvasWorkerInstance({ instanceId }) {
    const oldInstance = this.canvasWorkerInstanceMap.get(instanceId);
    if (oldInstance) {
      return oldInstance;
    }
    const canvasWorkerIndex =
      this.canvasWorkerIndex++ % this.canvasWorkers.length;
    const canvasWorker = this.canvasWorkers[canvasWorkerIndex];
    const newInstance = {
      ...canvasWorker,
      worker: canvasWorker,
      get workerId() {
        return canvasWorker.workerId;
      },
      addEventListener: (eventName, callback) => {
        if (eventName === "message") {
          return canvasWorker.addEventListener("message", (event) => {
            if (event?.data?.instanceId === instanceId) {
              callback(event);
            }
          });
        }
        return canvasWorker.addEventListener(...arguments);
      },
      postMessage: (msg, transferList) => {
        return canvasWorker.postMessage({ ...msg, instanceId }, transferList);
      },
      id: instanceId,
      uuid: v4(),
      terminate: () =>
        canvasWorker.postMessage({ dropInstance: true, instanceId }),
    };
    this.canvasWorkerInstanceMap.set(instanceId, newInstance);
    return newInstance;
  }

  onRemove(patient) {
    return _.get(this.store.selectedListOption, "value.type") ===
      "multi_patient"
      ? (e) => {
          e && e.stopPropagation();
          const multiPatients = _.get(
            this.props.store.prefs,
            "userPreferences.client.pm.multiPatients",
            [],
          );
          _.remove(multiPatients, (asmpid) => asmpid === patient.asmpid);
          this.store.patients = this.store.limitedPatients.filter(
            (p) => p.asmpid !== patient.asmpid,
          );
          this.props.store.setPref("pm.multiPatients", _.uniq(multiPatients));
        }
      : null;
  }

  selectPatient(patient) {
    if (this.store.patient) {
      this.store.setPatient(undefined);
    } else if (this.store.allowSingle) {
      this.store.setPatient(patient);
      this.store.postOpsLog({
        operationType: "patientSelected",
        extraContext: {
          asmpid: patient.asmpid,
          timestamp: new Date().toISOString(),
          user: sessionStorage.username,
        },
      });
    }
  }

  resizeRendererToDisplaySize = () => {
    const canvas = this.canvas;
    if (!canvas) return;
    const width = parseInt(canvas.clientWidth);
    const height = parseInt(canvas.clientHeight);
    const needResize = canvas.width !== width || canvas.height !== height;

    this.store.canvasRect = this.canvas.getBoundingClientRect();
    if (needResize) {
      this.renderWorker.postMessage({
        resizeCanvas: { canvasRect: this.store.canvasRect },
      });
    }
    return needResize;
  };
  togglePatientList = (e) => {
    e.preventDefault();
    this.store.showPatientList = !this.store.showPatientList;
  };

  selectLayout = (event, layout) => {
    event?.preventDefault();
    this.store.splitScreen = this.store.layout = layout;
    this.store.measurements = undefined;
  };

  _changeFontSize = action((e) => {
    // calls the changeFontSize function on AbstactPatients
    this.changeFontSize(e);
    this.store.resetMeasurements();
  });

  onResort = () => {
    requestAnimationFrame(() => {
      this.eventTarget.dispatchEvent(new CustomEvent("resize"));
    });
  };
  onDragEnd = () => {
    requestAnimationFrame(() => {
      this.eventTarget.dispatchEvent(new CustomEvent("resize"));
    });
  };

  resetOnWake() {
    const listener = () => {
      const visible = !document.hidden;
      this.visible = visible;
      this.store.resetMeasurements();
      if (visible) {
        this.wake();
      } else {
        this.sleep();
      }
    };
    document.addEventListener("visibilitychange", listener);
    this.disposers.push(() =>
      document.removeEventListener("visibilitychange", listener),
    );
  }
  sleep() {
    this.stopPolling();
    [...this.canvasWorkerInstanceMap.values()].forEach((i) =>
      i.postMessage({ resetLiveMode: true }),
    );
    this.renderWorker.postMessage({ clearAllLines: true });
  }
  wake() {
    this.store.stale = true;
    this.startPolling().then(() => {
      this.store.stale = false;
    });
  }
}

export default inject("store")(observer(Surveil));
